import { flatten, isEmpty as isEmptyLodash, pickBy } from "lodash/fp";
import type { LodashIsEmpty } from "lodash/fp";

import type { ResponseError } from "@app/common/api";

/**
 * Dupe of lodash.omit (to be deprecated in next lodash version)
 * @param keysToOmit single key or list of keys to remove from the object
 * @param obj object to remove keys from
 * @return copy of object with omitted keys
 * */
export const omit = <T extends object>(
  keysToOmit: string | string[],
  obj: T
) => {
  const clonedObject = { ...obj };
  const keys: string[] = flatten([keysToOmit]);
  for (const key of keys) {
    delete clonedObject[key as keyof T];
  }

  return clonedObject as Exclude<T, (typeof keys)[number]>;
};

/** Wrapper around lodash.isEmpty that includes explicit null check to appease
 * type checks */
export const isEmpty = (arg: Parameters<LodashIsEmpty>[0]) => {
  return arg == null || isEmptyLodash(arg);
};

export const delay = async (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

/** Removes all object keys with empty values (null, undefined, or "") */
export const sanitize = <T extends object>(obj: T) => {
  return pickBy((val) => val != null && val !== "", obj);
};

/**
 * Util for determining whether a given value is within the provided range of values.
 * Is *start inclusive, end non-inclusive* by default; can pass options to use an inclusive
 * end or non-inclusive start.
 * */
export const isWithinRange = (
  value: number,
  range: [number, number],
  options?: { inclusiveStart?: boolean; inclusiveEnd?: boolean }
): boolean => {
  const [start, end] = range;

  const isAfterRangeStart =
    options?.inclusiveStart ?? true ? start <= value : start < value;

  const isBeforeRangeEnd =
    options?.inclusiveEnd ?? false ? end >= value : end > value;

  return isAfterRangeStart && isBeforeRangeEnd;
};

export const isResponseError = (error: any): error is ResponseError => {
  return error && "response" in error;
};

export const isResponseErrorWithStatus = (
  error: unknown,
  status: number | number[]
): error is ResponseError => {
  const statuses = typeof status === "number" ? [status] : status;
  return isResponseError(error) && statuses.includes(error.response.status);
};

/**
 * Will determine if the error has a retryable status. Defaults to responses <400 or >499.
 * @param error error to check the status of
 * @param excludedRange range of statuses that are *not* retryable
 * */
export const hasRetryableStatus = (
  error?: unknown,
  excludedRange: [number, number] = [400, 499]
) => {
  return (
    !isResponseError(error) ||
    !isWithinRange(error.response.status, excludedRange, {
      inclusiveStart: true,
      inclusiveEnd: true,
    })
  );
};
